// @flow
import * as React from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import type {PaymentProvider, Store} from '../../../../entities';
import {useEffect, useState} from 'react';
import {loadStripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';
import CheckoutForm from './CheckoutForm';
import {colors} from '../../../../assets/styles/colors';
import type {StripeInitialization} from '../../duck';
import {actions as CheckoutActions} from '../../duck';
import {actions as AccountActions} from '../../../profile/account/duck';
import {connect} from 'react-redux';
import type {Dispatch} from 'redux';
import {bindActionCreators} from 'redux';
import type {RootState} from '../../../index';
import {Redirect} from 'react-router-dom';
import {CircularProgress} from '@material-ui/core';
import type {TierDetail} from '../../../tiers/api';

type Props = {
  paymentProvider: PaymentProvider,
  disabled?: boolean,
  setIsPaying: () => void,
  stripeInitialization: StripeInitialization,
  stripeUpdate: ({
    subscribeUpdateRequest: {
      automaticCharge: boolean,
    },
  }) => void,
  store?: Store,
  type?: string,
  planSelected?: TierDetail,
  additionalParams?: {[key: string]: string},
  redirectOnFail?: boolean,
  inlineError?: boolean,
};

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  checkbox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1, 1, 1),
  },
  checkboxLabel: {
    marginTop: theme.spacing(1),
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  automaticChargeDisclaimer: {
    margin: theme.spacing(-1, 0, 1, 3.5),
    color: colors.grey700,
    fontSize: '0.75rem',
  },
}));

function StripeCheckoutForm(props: Props) {
  const {
    paymentProvider,
    // disabled,
    setIsPaying,
    stripeInitialization,
    // stripeUpdate,
    // store,
    type,
    planSelected,
    redirectOnFail = true,
    inlineError = false,
    additionalParams,
  } = props;

  const classes = useStyles();

  const [stripePromise, setStripePromise] = useState(null);

  // const [automaticCharge, setAutomaticCharge] = useState(true);

  const loadLoadStripe = async pk => {
    const stripePromise = await loadStripe(pk);
    setStripePromise(stripePromise);
  };

  useEffect(() => {
    if (paymentProvider && paymentProvider.serverParameters.publicKey) {
      loadLoadStripe(paymentProvider.serverParameters.publicKey);
    }
  }, [paymentProvider]);

  // const handleSaveAutomaticCharge = e => {
  //   e.persist();
  //   stripeUpdate({
  //     subscribeUpdateRequest: {
  //       automaticCharge: !automaticCharge,
  //     },
  //   });
  //   setAutomaticCharge(!automaticCharge);
  // };

  const options = {
    clientSecret: stripeInitialization && stripeInitialization.clientSecret,
    appearance: {
      theme: 'stripe',
      labels: 'floating',
      variables: {
        fontFamily: '"Poppins", "Muli", sans-serif',
        borderRadius: '8px',
        colorPrimary: colors.purple3,
        colorTextPlaceholder: colors.grey600,
      },
      rules: {
        '.Block': {
          boxShadow: 'none',
        },
        '.Input': {
          padding: '10px 12px',
          boxShadow: 'none',
        },
        '.Label--resting': {
          color: colors.grey600,
        },
        '.Label--floating': {
          color: colors.grey600,
        },
        // '.Input:disabled, .Input--invalid:disabled': {
        //   color: 'lightgray'
        // },
        '.Tab': {
          padding: '10px 12px 8px 12px',
          boxShadow: 'none',
        },
        '.Tab:hover': {
          boxShadow:
            '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
        },
        '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
          boxShadow:
            '0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
        },
      },
    },
    locale: 'es-419',
  };

  const [stripeReady, setStripeReady] = useState(false);

  return stripeInitialization && stripeInitialization.clientSecret ? (
    <div className={classes.form}>
      <Elements options={options} stripe={stripePromise}>
        <CheckoutForm
          additionalParams={additionalParams}
          inlineError={inlineError}
          setIsPaying={setIsPaying}
          setStripeReady={setStripeReady}
          type={type}
          planSelectedId={planSelected ? planSelected.id : undefined}
          userCardProfileId={
            stripeInitialization.userCardProfileId
              ? stripeInitialization.userCardProfileId
              : undefined
          }
        />
      </Elements>
      {!stripeReady && <CircularProgress style={{alignSelf: 'center'}} />}
    </div>
  ) : redirectOnFail ? (
    <Redirect to="/checkout" />
  ) : (
    '...'
  );
}

StripeCheckoutForm.propTypes = {
  paymentProvider: PropTypes.object,
  disabled: PropTypes.bool,
  setIsPaying: PropTypes.func.isRequired,
  stripeInitialization: PropTypes.object,
  // automaticCharge: PropTypes.bool,
  stripeUpdate: PropTypes.func,
  store: PropTypes.object,
  type: PropTypes.string,
  planSelected: PropTypes.object,
};

export default connect(
  (state: RootState) => ({
    store: state.store.store,
    stripeInitialization: state.checkout.stripeInitialization,
    planSelected: state.tiersList.planSelected,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        ...CheckoutActions,
        ...AccountActions,
      },
      dispatch
    )
)(StripeCheckoutForm);
